import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiExternalLink } from 'react-icons/fi';

gsap.registerPlugin(ScrollTrigger);

const DesignRush = () => {
  const sectionRef = useRef(null);
  const leftColRef = useRef(null);
  const rightColRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Content Animation (Left Column)
      gsap.fromTo(
        leftColRef.current.children,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
          },
        }
      );

      // Image Animation (Right Column)
      gsap.fromTo(
        rightColRef.current,
        { x: 50, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="designrush"
      ref={sectionRef}
      className="w-full py-8 md:py-20 bg-transparent"
    >
      <div className="relative z-10 container-custom">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-12 items-center">
          
          {/* LEFT COLUMN: Content */}
          <div ref={leftColRef} className="flex flex-col items-start text-left order-2 lg:order-1 lg:pr-8">
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/10 text-[#F2B135] text-xs font-bold tracking-widest uppercase mb-6 backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
              Recognized Among Top Agencies
            </div>

            <h2
              className="text-3xl md:text-5xl font-black leading-[1.2] text-white mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              RojrzTech Joins <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F2B135] to-[#ffaa00]">DesignRush</span> <span className="text-white">✅</span>
            </h2>

            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 font-medium">
              RojrzTech is excited to announce that we are on the verge of being featured on DesignRush under the category of <strong>‘Digital Marketing Agencies in Pakistan.’</strong> This recognition highlights our commitment to delivering exceptional services in digital marketing, SEO, SMM, and more. Once live, our profile will represent our innovative approach and dedication to excellence. Stay tuned as we continue to set new benchmarks in the industry.
            </p>

            {/* CTA Button */}
            <a 
              href="https://www.designrush.com/agency/profile/rojrztech" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-animated-gold"
            >
              View Our Profile <FiExternalLink size={18} />
            </a>

          </div>

          {/* RIGHT COLUMN: Image */}
          <div ref={rightColRef} className="relative order-1 lg:order-2">
            {/* Main Image Container */}
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] group z-10 bg-white/5 flex items-center justify-center p-8">
              {/* Demo Image (User will replace with DesignRush logo later) */}
              <img
                src="desingrush.png"
                alt="DesignRush Partnership"
                className="w-full h-auto object-cover aspect-[4/3] rounded-xl transform transition-transform duration-700 group-hover:scale-105 opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#F2B135]/20 to-transparent mix-blend-overlay pointer-events-none" />
              
              {/* Overlay Badge to make it look like a partnership announcement */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                 <div className="bg-black/60 backdrop-blur-md border border-[#F2B135]/50 px-6 py-3 rounded-full text-white font-bold tracking-widest uppercase text-sm shadow-xl">
                   Partnership
                 </div>
              </div>
            </div>

            {/* Glowing aura */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100%] h-[100%] bg-[#F2B135]/10 blur-[80px] rounded-full z-0 pointer-events-none" />
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default DesignRush;
